import { useRef, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Users } from 'lucide-react';
import { PanelFrame } from '../panel-frame.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import type { PanelId, TabId } from '../store.js';
import { useCatchmentLink, useCatchmentKey } from '../catchment-link.js';
import { useCensus } from '../../api/census-hooks.js';
import type { CensusResponse } from '../../api/client.js';

// Census (Catchment Details) — demographic + workforce profile of the catchment
// polygon broadcast by the Catchment panel (see catchment-link). Record-style:
// header + KPI strip + tabs (Demographics · Staffing · Pets). Read-only.

const CCY: Record<string, string> = { GB: '£', FR: '€', UK: '£', France: '€' };

const fmtInt = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString('en-GB');
const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Income is shown as a national percentile (50 = market median), not a currency
// value: the underlying source is currency income in some markets (£/€) and an
// ordinal deprivation rank in others — only a relative standing compares across
// markets. Rendered as an ordinal, e.g. 63 → "63rd".
function fmtPercentile(n: number | null | undefined) {
  if (n == null) return '—';
  const r = Math.round(n);
  const v = r % 100;
  const suffix = ['th', 'st', 'nd', 'rd'][(v - 20) % 10] || ['th', 'st', 'nd', 'rd'][v] || 'th';
  return `${r}${suffix}`;
}
// Canonical display split — a common 6-band, 15-year scheme that every target
// market collapses into. It's the NATIVE format of FR/BE/DK/IT/NO/SE; a clean
// sum for the 5-year markets (UK, Ireland); and Germany/NL/Spain/Scotland fold
// in by year-overlap (proportional where a native band straddles a boundary).
// One scheme means the pyramid reads the same regardless of source granularity.
const CANON_AGE_BANDS: ReadonlyArray<readonly [number, number]> = [
  [0, 14],
  [15, 29],
  [30, 44],
  [45, 59],
  [60, 74],
  [75, 200], // open-ended top band
];
function canonAgeLabel([lo, hi]: readonly [number, number]): string {
  return hi >= 200 ? `${lo}+` : `${lo}–${hi}`;
}
// Collapse a market's native age_* bands into CANON_AGE_BANDS, distributing each
// native band across the canon bands it overlaps (by share of years). Returns a
// count per canon band, in CANON_AGE_BANDS order.
function bucketCanonAges(age: Record<string, number | null>): number[] {
  const out = CANON_AGE_BANDS.map(() => 0);
  for (const [key, raw] of Object.entries(age)) {
    if (!key.startsWith('age_')) continue;
    const p = key.slice('age_'.length).split('_');
    const lo = parseInt(p[0]!, 10);
    if (Number.isNaN(lo)) continue;
    const hi = p.length < 2 || p[1] === 'plus' ? 100 : parseInt(p[1]!, 10);
    const v = Number(raw) || 0;
    const span = hi - lo + 1;
    if (span <= 0) continue;
    CANON_AGE_BANDS.forEach(([c0, c1], i) => {
      const a = Math.max(lo, c0);
      const b = Math.min(hi, Math.min(c1, 100));
      const overlap = b - a + 1;
      if (overlap > 0) out[i]! += v * (overlap / span);
    });
  }
  return out;
}

export function CensusPanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  initialState?: Record<string, unknown>;
}) {
  const catchKey = useCatchmentKey(tabId, panelId);
  const broadcast = useCatchmentLink((s) => s.byKey[catchKey] ?? null);
  // A Census opened cold (command palette / search bar) must NOT inherit a
  // catchment that's already broadcasting on the channel at mount — it opens
  // blank, ready to follow the NEXT broadcast (same fix as the Catchment panel).
  // Only a Census deliberately opened to follow (the Catchment panel's "Census"
  // button passes followLinkOnOpen) shows the current catchment on mount.
  const followOnOpen = initialState?.['followLinkOnOpen'] === true;
  const mountBroadcastRef = useRef(broadcast);
  const active = followOnOpen || broadcast !== mountBroadcastRef.current ? broadcast : null;
  const [tab, setTab] = useState('demographics');
  const { data, isLoading, isError } = useCensus(active);
  const sym = active ? (CCY[active.market] ?? '') : '';

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="cs-root">
        <style>{CS_CSS}</style>

        {!active ? (
          <PanelEmptyState fill icon={<Users size={18} />} title="Profile a catchment">
            Open <b>Catchment</b>, drop a location and pick a sub-segment — its drive-time isochrone
            profiles the resident population and clinician supply here automatically.
          </PanelEmptyState>
        ) : isLoading ? (
          <div className="cs-empty">Loading catchment profile…</div>
        ) : isError ? (
          <div className="cs-empty">Couldn’t load the catchment profile.</div>
        ) : !data || data.meta.areas_in_catchment === 0 ? (
          <div className="cs-empty">No population data for this catchment.</div>
        ) : (
          <>
            <div className="cs-kpis">
              <Kpi label="People" value={fmtInt(data.people)} />
              <Kpi label="Households" value={fmtInt(data.households)} />
              <Kpi
                label="Income percentile"
                value={fmtPercentile(data.income_percentile)}
                sub="vs national"
              />
              <Kpi
                label="Demand index"
                value={data.demand_index == null ? '—' : String(data.demand_index)}
              />
            </div>

            <Tabs.Root value={tab} onValueChange={setTab} className="cs-tabs">
              <Tabs.List className="cs-tablist">
                {['demographics', 'staffing', 'pets'].map((k) => (
                  <Tabs.Trigger key={k} value={k} className="cs-tab">
                    {k[0]!.toUpperCase() + k.slice(1)}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              <Tabs.Content value="demographics" className="cs-body">
                <Demographics data={data} />
              </Tabs.Content>
              <Tabs.Content value="staffing" className="cs-body">
                <Staffing data={data} sym={sym} />
              </Tabs.Content>
              <Tabs.Content value="pets" className="cs-body">
                <Pets data={data} />
              </Tabs.Content>
            </Tabs.Root>
          </>
        )}
      </div>
    </PanelFrame>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="cs-kpi">
      <div className="cs-kpi-lab">{label}</div>
      <div className="cs-kpi-val">{value}</div>
      {sub && <div className="cs-kpi-sub">{sub}</div>}
    </div>
  );
}

function Demographics({ data }: { data: CensusResponse }) {
  const counts = bucketCanonAges(data.age_structure);
  // Oldest at top, to match a standard pyramid read.
  const display = CANON_AGE_BANDS.map((band, i) => ({ band, v: counts[i]! })).reverse();
  const maxB = Math.max(1, ...counts);
  const people = data.people || 1;
  return (
    <div>
      <div className="cs-sec">Age profile</div>
      <div className="cs-pyramid">
        {display.map(({ band, v }) => (
          <div key={band[0]} className="cs-prow">
            <span className="cs-plab">{canonAgeLabel(band)}</span>
            <span className="cs-pbar">
              <span style={{ width: `${(100 * v) / maxB}%` }} />
            </span>
            <span className="cs-ppop">{fmtInt(v)}</span>
            <span className="cs-ppct cs-dim">{Math.round((1000 * v) / people) / 10}%</span>
          </div>
        ))}
      </div>

      <div className="cs-sec" style={{ marginTop: 14 }}>
        Sex split
      </div>
      {data.sex ? (
        <>
          <div className="cs-sexbar">
            <span className="cs-sex-f" style={{ width: `${data.sex.female_pct}%` }} />
            <span className="cs-sex-m" style={{ width: `${data.sex.male_pct}%` }} />
          </div>
          <div className="cs-sexlab">
            <span>♀ {data.sex.female_pct}%</span>
            <span>{data.sex.male_pct}% ♂</span>
          </div>
        </>
      ) : (
        <div className="cs-dim cs-note">Sex not published at this grain.</div>
      )}

      <div className="cs-note">
        Demand index: 100 = national-average healthcare demand for this age mix (modelled).
        {data.income_percentile != null
          ? ` Income percentile: where this catchment sits in the national income
             distribution (50 = market median), so it compares across markets whose
             source is currency income or a deprivation rank — ${data.income_basis}.`
          : ''}
      </div>
    </div>
  );
}

function Staffing({ data, sym }: { data: CensusResponse; sym: string }) {
  void sym;
  const rows = Object.entries(data.clinical_supply);
  // Which rows used their region's own density rather than the national figure.
  // Both regimes coexist in one catchment — a market publishes regional density
  // for some professions and not others — so state it per row and in the note.
  // Until 2026-08-02 this always read "national": the Catchment panel hardcoded
  // region: null, so the regional rows we hold were never requested.
  const regional = rows.filter(([, r]) => r.density_basis?.startsWith('regional')).length;
  const basis =
    regional === 0
      ? 'national density'
      : regional === rows.length
        ? 'this region’s own density'
        : `this region’s own density for ${regional} of ${rows.length}, national for the rest`;
  return (
    <div>
      <div className="cs-supply">
        {rows.map(([sub, row]) => (
          <div key={sub} className="cs-srow">
            {row.available === null ? (
              <>
                <span className="cs-slab">{sub}</span>
                <span className="cs-dim cs-snote">no figure</span>
              </>
            ) : (
              <>
                <span className="cs-slab">{sub}</span>
                <span className="cs-sval" title={row.density_basis ?? undefined}>
                  {fmtInt(row.est_clinicians_in_catchment)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="cs-note">
        Estimated headcount = clinician density (per 100k, national health-workforce statistics) ×
        catchment population, at {basis}.
      </div>
    </div>
  );
}

function Pets({ data }: { data: CensusResponse }) {
  const p = data.pets;
  if (!('est_dogs' in p)) return <div className="cs-dim cs-note">{p.note}</div>;
  const hh = data.households || 0;
  const pct = (owning: number) => (hh ? `${Math.round((100 * owning) / hh)}% of households` : '');
  // label, value, optional share-of-households hint
  const rows: Array<[string, string, string]> = [
    ['Estimated dogs', fmtInt(p.est_dogs), ''],
    ['Estimated cats', fmtInt(p.est_cats), ''],
    ['Dog-owning households', fmtInt(p.dog_owning_households), pct(p.dog_owning_households)],
    ['Cat-owning households', fmtInt(p.cat_owning_households), pct(p.cat_owning_households)],
  ];
  return (
    <div>
      <table className="cs-table">
        <tbody>
          {rows.map(([label, value, hint]) => (
            <tr key={label}>
              <td className="cs-td-lab">
                {label}
                {hint && <span className="cs-dim"> · {hint}</span>}
              </td>
              <td className="cs-td-val">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cs-note">
        {capitalize(p.basis)}. Dog- and cat-owning shares overlap — some households own both — so
        they don’t sum to total pet ownership.
      </div>
    </div>
  );
}

const CS_CSS = `
.cs-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.cs-empty { padding:var(--s-4); color:var(--text-tertiary); line-height:1.5; }
.cs-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:var(--s-2); padding:var(--s-3); }
.cs-kpi { background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); padding:9px 11px; display:flex; flex-direction:column; gap:3px; min-width:0; }
.cs-kpi-lab { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.06em; color:var(--text-tertiary); font-weight:600; }
.cs-kpi-val { font-size:15px; font-weight:700; letter-spacing:-0.015em; line-height:1.1; color:var(--text-primary); }
.cs-kpi-sub { font-size:var(--t-meta); color:var(--text-tertiary); }
.cs-tabs { display:flex; flex-direction:column; min-height:0; flex:1; }
.cs-tablist { display:flex; gap:16px; padding:6px var(--s-3) 0; border-bottom:1px solid var(--hairline); }
.cs-tab { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-tertiary); background:none; border:none; border-bottom:2px solid transparent; padding:4px 0 6px; cursor:pointer; }
.cs-tab[data-state="active"] { color:var(--text-primary); font-weight:600; border-bottom-color:var(--accent); }
.cs-body { flex:1; overflow:auto; padding:var(--s-3); min-height:0; }
.cs-sec { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-secondary); font-weight:700; margin-bottom:7px; border-top:2px solid var(--text-primary); padding-top:7px; }
/* The first section in a tab sits directly under the tabs row — drop its top
   rule so there's no thick line between the tabs and the content (e.g. the age
   pyramid). The rule stays as a separator between later sections. */
.cs-body > div > .cs-sec:first-child { border-top:none; padding-top:0; }
.cs-pyramid { display:flex; flex-direction:column; gap:2px; }
.cs-prow { display:grid; grid-template-columns:52px 1fr 72px 48px; align-items:center; gap:8px; font-size:var(--t-body); }
.cs-plab { color:var(--text-secondary); text-align:right; }
.cs-pbar { height:11px; background:transparent; border-radius:2px; overflow:hidden; }
.cs-pbar > span { display:block; height:100%; background:var(--accent); }
.cs-ppop { text-align:right; font-family:var(--font-mono,monospace); }
.cs-ppct { text-align:right; font-family:var(--font-mono,monospace); }
.cs-dim { color:var(--text-tertiary); }
.cs-sexbar { display:flex; height:11px; border-radius:2px; overflow:hidden; border:1px solid var(--hairline); }
.cs-sex-f { background:var(--accent); }
.cs-sex-m { background:var(--color-accent-300, #d9b48f); }
.cs-sexlab { display:flex; justify-content:space-between; font-size:var(--t-body); color:var(--text-secondary); margin-top:5px; }
.cs-note { font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.45; margin-top:12px; }
.cs-callout { background:var(--menu-surface); border:1px solid var(--hairline); border-radius:4px; padding:9px 11px; font-size:var(--t-body); line-height:1.45; color:var(--text-secondary); }
.cs-callout b { color:var(--text-primary); }
.cs-supply { display:flex; flex-direction:column; }
.cs-srow { display:flex; justify-content:space-between; align-items:baseline; gap:10px; padding:7px 0; border-bottom:1px solid var(--hairline); }
.cs-slab { font-size:var(--t-body); color:var(--text-primary); }
.cs-sval { font-size:var(--t-body); font-weight:600; text-align:right; white-space:nowrap; }
.cs-snote { font-size:var(--t-meta); }
.cs-table { width:100%; border-collapse:collapse; }
.cs-table td { padding:7px 0; border-bottom:1px solid var(--hairline); font-size:var(--t-body); }
.cs-table tr:last-child td { border-bottom:none; }
.cs-td-lab { color:var(--text-secondary); }
.cs-td-val { text-align:right; font-weight:600; color:var(--text-primary); font-family:var(--font-mono,monospace); white-space:nowrap; }
`;
